package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onFocusIn
import com.varabyte.kobweb.compose.ui.modifiers.onFocusOut
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.until
import kotlin.coroutines.resume
import kotlin.js.Date
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

enum class LToastVariant {
    NEUTRAL,
    INFO,
    SUCCESS,
    WARNING,
    DANGER,
}

/**
 * What a caller passes when it cannot reach [toastDefaultDurationMillis].
 *
 * Toasts delivered through a `(String, LToastVariant, Long) -> Int` callback — which is how every
 * page hands `ToastState.show` to the composables under it — have to name a duration, so failure
 * toasts across settings and the project pages passed `6_000` and silently opted out of the rule
 * below. "Couldn't delete this file. Retry?" is exactly as actionable as the editor's
 * "Couldn't save this translation. Retry?", and one of them used to vanish while the other waited.
 */
const val TOAST_UNTIL_DISMISSED: Long = 0L

/**
 * "The caller did not say" — the duration is then [toastDefaultDurationMillis]'s to decide.
 *
 * A sentinel rather than a default expression, because [ToastState.show] and
 * [ToastState.showWithAction] have to reach the same rule and only one of them knows whether there
 * is an action. Spelling the rule out in `show`'s default would leave two copies of it a parameter
 * apart, and the copy that got edited would be whichever one the next reader found first.
 *
 * It is negative rather than zero because zero already means something here: [TOAST_UNTIL_DISMISSED]
 * is a duration a caller deliberately asks for, and "wait for me" must not be confused with
 * "decide for me" — they agree for a failure and disagree for everything else.
 */
const val TOAST_DURATION_UNSET: Long = -1L

/**
 * How long a toast stays up before [ToastState.show] is told otherwise.
 *
 * A failed save's explanation used to run on the identical 4-second clock as its own confirmation
 * of success — the one message a reader most needs time to actually read had exactly as long on
 * screen as one that needed no reading at all. WARNING and DANGER persist until the reader
 * dismisses them (or the caller passes its own `durationMillis`, as the editor's conflict toast
 * does); everything with nothing to act on keeps the 4 seconds.
 *
 * **A toast with an action is never on a clock, whatever its variant.** "Saved · Undo" is a SUCCESS
 * toast and by the rule above it would have four seconds — which is the length of time it takes to
 * read it, notice the word Undo, and reach for a control that is no longer there. An offer that
 * expires while you are deciding whether to take it is worse than no offer: the reader now believes
 * they can undo this and has no way to. Anything with something to act on waits to be dismissed.
 */
fun toastDefaultDurationMillis(variant: LToastVariant, hasAction: Boolean): Long = when {
    hasAction -> TOAST_UNTIL_DISMISSED
    variant == LToastVariant.WARNING || variant == LToastVariant.DANGER -> TOAST_UNTIL_DISMISSED
    else -> 4_000L
}

data class LToastMessage(
    val id: Int,
    val message: String,
    val variant: LToastVariant,
    /** The one thing to do about this message, if there is one. Rendered as a GHOST button. */
    val actionLabel: String? = null,
    val onAction: (() -> Unit)? = null,
    val dismissing: Boolean = false,
)

/**
 * How many toasts stay visible at once, oldest dropped first.
 *
 * Warning and danger now persist until dismissed rather than clearing themselves — which is
 * correct for a message someone needs to act on, and also means an unattended session can pile
 * them up with nothing clearing the column. Three is enough to see a burst of related failures
 * without the stack running off the bottom of the viewport.
 */
private const val MAX_VISIBLE_TOASTS = 3

@Stable
class ToastState(private val scope: CoroutineScope) {
    private var nextId = 1
    private val messages = mutableStateListOf<LToastMessage>()
    private val timers = mutableMapOf<Int, ToastTimer>()

    val current: List<LToastMessage>
        get() = messages.filterNot(LToastMessage::dismissing)

    internal val rendered: List<LToastMessage>
        get() = messages

    /**
     * Puts [message] on screen and returns the id that dismisses it.
     *
     * Leave [durationMillis] alone unless this message has a reason of its own to outlast the rule
     * in [toastDefaultDurationMillis] — the editor's conflict toast does, because a conflict is
     * read against the pane behind it.
     *
     * **Three parameters, and it has to stay three.** Fourteen files declare a
     * `notify: (String, LToastVariant, Long) -> Int` parameter and hand this method to it as
     * `toasts::show`; one of them writes `val notify = toasts::show` with no expected type at all,
     * which makes Kotlin infer the reference's *full* arity rather than adapting it. Two more
     * defaulted parameters here — or a second `show` overload for the reference to choose between —
     * breaks that line and roughly twenty call sites behind it. [showWithAction] carries the toasts
     * that have something to press.
     */
    fun show(
        message: String,
        variant: LToastVariant = LToastVariant.NEUTRAL,
        durationMillis: Long = TOAST_DURATION_UNSET,
    ): Int = enqueue(message, variant, durationMillis, actionLabel = null, onAction = null)

    /**
     * A toast with the one thing to do about it: "Saved 12 keys · Undo", "Upload failed · Retry".
     *
     * Waits to be dismissed by default, whatever [variant] would otherwise have said — see
     * [toastDefaultDurationMillis] for why an offer must not expire while it is being read.
     *
     * Separate from [show] rather than two more parameters on it, for the callable-reference reason
     * documented there. The label is an action a control is naming, so it is a verb and it names the
     * thing: "Undo", "Retry", "Open the job" — never "OK", and never "Click here".
     */
    fun showWithAction(
        message: String,
        actionLabel: String,
        onAction: () -> Unit,
        variant: LToastVariant = LToastVariant.NEUTRAL,
        durationMillis: Long = TOAST_DURATION_UNSET,
    ): Int = enqueue(message, variant, durationMillis, actionLabel, onAction)

    private fun enqueue(
        message: String,
        variant: LToastVariant,
        durationMillis: Long,
        actionLabel: String?,
        onAction: (() -> Unit)?,
    ): Int {
        val id = nextId++
        val duration = if (durationMillis < 0) {
            toastDefaultDurationMillis(variant, actionLabel != null)
        } else {
            durationMillis
        }
        messages += LToastMessage(id, message, variant, actionLabel, onAction)
        // Oldest first — it has had the most time to be read already.
        if (current.size > MAX_VISIBLE_TOASTS) {
            dismiss(current.first().id)
        }
        if (duration > 0) {
            timers[id] = ToastTimer(scope, duration) { dismiss(id) }
        }
        return id
    }

    /** Suspends [id]'s countdown without losing its place — called while the reader is on it. */
    fun pause(id: Int) {
        timers[id]?.pause()
    }

    /** Resumes [id]'s countdown from wherever [pause] left it. */
    fun resume(id: Int) {
        timers[id]?.resume()
    }

    fun dismiss(id: Int) {
        val index = messages.indexOfFirst { it.id == id && !it.dismissing }
        if (index == -1) return
        timers.remove(id)?.cancel()
        messages[index] = messages[index].copy(dismissing = true)
        scope.launch {
            if (!prefersReducedMotion()) delay(Motion.OverlayExitMs.toLong())
            messages.removeAll { it.id == id }
        }
    }

    fun clear() {
        messages.clear()
        timers.values.forEach { it.cancel() }
        timers.clear()
    }
}

/**
 * One toast's auto-dismiss countdown, pausable without losing its place.
 *
 * `scope.launch { delay(...) }` alone cannot pause: cancelling that job to stop the clock loses
 * the remaining time, and there is no "un-cancel" a coroutine. This records how much time was left
 * when [pause] was last called and [resume]s from there instead of from the full duration again.
 *
 * [pause] and [resume] are reference-counted, because hover and focus each call them
 * independently: tabbing to the close button while the pointer is still over the toast, then
 * moving the pointer away, must not resume the clock while focus is still on it. The countdown
 * only actually restarts once every caller that paused it has also released it.
 */
private class ToastTimer(
    private val scope: CoroutineScope,
    initialRemainingMillis: Long,
    private val onExpire: () -> Unit,
) {
    private var remainingMillis = initialRemainingMillis
    private var startedAtMillis = 0.0
    private var job: Job? = null
    private var pauseCount = 0

    init {
        start()
    }

    private fun start() {
        startedAtMillis = Date().getTime()
        job = scope.launch {
            delay(remainingMillis)
            onExpire()
        }
    }

    fun pause() {
        pauseCount++
        val running = job ?: return
        running.cancel()
        job = null
        remainingMillis = (remainingMillis - (Date().getTime() - startedAtMillis).toLong()).coerceAtLeast(0)
    }

    fun resume() {
        pauseCount = (pauseCount - 1).coerceAtLeast(0)
        if (pauseCount > 0 || job != null) return
        start()
    }

    fun cancel() {
        job?.cancel()
        job = null
    }
}

@Composable
fun rememberToasts(): ToastState {
    val scope = rememberCoroutineScope()
    return remember(scope) { ToastState(scope) }
}

val LocalToasts = compositionLocalOf<ToastState> {
    error("LocalToasts is not provided. Create a state with rememberToasts().")
}

private fun LToastVariant.tone(palette: SitePalette): Color = when (this) {
    LToastVariant.NEUTRAL -> palette.textSecondary
    LToastVariant.INFO -> palette.info
    LToastVariant.SUCCESS -> palette.success
    LToastVariant.WARNING -> palette.warning
    LToastVariant.DANGER -> palette.danger
}

/**
 * A toast rising into place.
 *
 * Transform only — never opacity. A suspended tab freezes an entrance on its opening frame, and an
 * opening frame of `opacity: 0` is a notification that never appears. The same rule that governs
 * [LReveal]: an entrance must not be able to hide content, least of all a message telling someone
 * their work was saved.
 *
 * **A transition, not a keyframe, and that is the whole reason this file changed.** The entrance
 * used to be `animation: l-toast-enter …`, and a running keyframe cannot be retargeted — so the
 * exit below had to open with `animation: none` to get out of it. That line discards the
 * interpolated transform in the same style change that declares the exit, which means a toast
 * dismissed while it is still arriving snaps back to its resting position before it leaves.
 * Dismissal mid-entrance is reachable in ordinary use: [ToastState.show] evicts the oldest toast
 * the moment a fourth arrives, and a burst of failures can land four inside one entrance.
 * [LMenuStyle] and [LModalStyle] have always driven both directions from `data-presence`
 * transitions, and a transition retargets from wherever it currently is; the toast was the one
 * appearing surface in the system that did not.
 *
 * The distances are the system's, for the same reason: [Motion.PopoverTravelPx] and
 * [Motion.PopoverEnterScale], not the 10px and `.98` this carried on its own. It used to enter from
 * one scale and leave to another, which nothing argued for.
 */
val LToastStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Card)
            // The one surface in the system with nothing beneath it to borrow separation from: a
            // toast lands over whatever page happens to be there, with no scrim and no trigger it
            // grew out of, so the shadow is what says it is not part of that page.
            .elevation(Elevation.Floating)
            .padding(Space.MD)
            // Fully visible at rest and while waiting for the first animation frame, exactly as
            // [LMenuStyle] is: the resting state is the readable one, and the phase below only ever
            // describes a departure from it.
            .styleModifier {
                property(
                    "transition",
                    "opacity ${Motion.StandardMs}ms ${Motion.EaseEntrance}, " +
                        "transform ${Motion.StandardMs}ms ${Motion.EaseEntrance}",
                )
            }
    }
    // Below where it belongs, because the column is anchored to the bottom of the viewport and a
    // notification that rises into that corner reads as arriving from off-screen.
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
            property(
                "transition",
                "opacity ${Motion.OverlayExitMs}ms ${Motion.EaseIn}, " +
                    "transform ${Motion.OverlayExitMs}ms ${Motion.EaseIn}",
            )
            property("pointer-events", "none")
        }
    }
}

val ToastHostStyle = CssStyle {
    base {
        Modifier
            .position(Position.Fixed)
            .right(Space.XXL)
            .bottom(Space.XXL)
            .width(24.cssRem)
            .maxWidth(24.cssRem)
            .zIndex(1100)
    }
    until(Breakpoint.MD) {
        Modifier
            .left(Space.LG)
            .right(Space.LG)
            .bottom(Space.LG)
            .width(100.percent)
            .maxWidth(100.percent)
            .styleModifier { property("width", "calc(100% - 32px)") }
    }
}

val LToastCloseStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Color.rgba(0, 0, 0, 0f))
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0f))
            .borderRadius(Radius.Control)
            // 40x40, the hit-area floor, rather than padding around a 14px glyph — which measured
            // 26x30 in the running gallery. This is the control you reach for while something else
            // is moving on screen, so it is the last one that should be hard to hit. The icon does
            // not grow; the target does, which is why the size is here and not on the glyph.
            .size(HIT_AREA_FLOOR_PX.px)
            .styleModifier { property("display", "inline-flex") }
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .padding(0.px)
            .cursor(Cursor.Pointer)
    }
    hover { Modifier.color(colorMode.toSitePalette().textPrimary) }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * One toast.
 *
 * **[onAction] dismisses the toast after it runs, and that is the component's decision rather than
 * the caller's.** A toast is the receipt for something that just happened, and its action is the
 * one way to change what happened — undo the save, retry the upload, open the job. Once that has
 * been pressed the receipt is out of date: leaving it up offers a second Undo for an undo that has
 * already run, which is a control that either does nothing or does damage depending on how the
 * handler was written. So the offer is taken once and the toast leaves.
 *
 * The countdown is already paused for as long as focus is anywhere inside the toast — the row's
 * `focusin`/`focusout` handlers are on the container, and both events bubble — so tabbing to the
 * action is enough to stop the clock without the action having to know anything about it. That
 * matters more now than it did: an action is the reason someone would ever tab into a toast at all.
 */
@Composable
fun LToast(
    message: String,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    variant: LToastVariant = LToastVariant.NEUTRAL,
    /**
     * Called on mouse-enter and on focus entering the toast. [ToastHost] wires this to
     * [ToastState.pause] so a toast someone is reading or has tabbed to does not disappear under
     * them.
     */
    onPause: () -> Unit = {},
    /** Called on mouse-leave and on focus leaving the toast; [ToastHost] wires this to [ToastState.resume]. */
    onResume: () -> Unit = {},
    /** The label of the one thing to do about this message. Null means there is nothing to do. */
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null,
) {
    val palette = com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette()
    val tone = variant.tone(palette)
    Row(
        LToastStyle.toModifier()
            .then(modifier)
            .onMouseEnter { onPause() }
            .onMouseLeave { onResume() }
            .onFocusIn { onPause() }
            .onFocusOut { onResume() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Div(Modifier.color(tone).padding(right = Space.MD).toAttrs()) {
            when (variant) {
                LToastVariant.NEUTRAL -> LCommentIcon()
                LToastVariant.INFO -> LInfoIcon()
                LToastVariant.SUCCESS -> LCheckIcon()
                LToastVariant.WARNING -> LWarningIcon()
                LToastVariant.DANGER -> LDangerIcon()
            }
        }
        Div(
            Modifier
                .flexGrow(1)
                .fontSize(TypeScale.BodySmall)
                .fontWeight(500)
                .lineHeight(TypeScale.BodyLineHeight)
                .toAttrs(),
        ) {
            Text(message)
        }
        if (actionLabel != null && onAction != null) {
            Div(
                Modifier
                    .padding(right = Space.XS)
                    .styleModifier { property("flex", "0 0 auto") }
                    .toAttrs { attr("data-toast-action", "true") },
            ) {
                LButton(
                    label = actionLabel,
                    onClick = {
                        onAction()
                        onDismiss()
                    },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                )
            }
        }
        Button(
            LToastCloseStyle.toModifier().toAttrs {
                type(ButtonType.Button)
                attr("aria-label", tr("ds.ds.ltoast.dismiss_notification", "Dismiss notification"))
                onClick { onDismiss() }
            },
        ) {
            LCloseIcon(Modifier.iconSize(IconSize.MD))
        }
    }
}

@Composable
fun ToastHost(
    state: ToastState = LocalToasts.current,
    modifier: Modifier = Modifier,
) {
    // Where the reader was before a toast took their focus.
    //
    // A toast is not modal and nobody navigates *to* one on purpose: they Tab through the page and
    // arrive in it. So when the last toast is dismissed, the honest place to put focus back is the
    // control they were on when it caught them — not the region, which sits at the end of the
    // document and would strand them there, and certainly not `<body>`.
    var focusOrigin by remember { mutableStateOf<HTMLElement?>(null) }

    Column(
        ToastHostStyle.toModifier()
            .gap(Space.SM)
            .attr("role", "region")
            .attr("aria-label", t("ds.ds.ltoast.notifications", "Notifications"))
            .attr("aria-live", "polite")
            // Focusable only from script: the last resort when the remembered origin has itself
            // been removed from the page while the toast was up.
            .attr("tabindex", "-1")
            .attr("data-toast-host", "true")
            .onFocusIn { event ->
                val from = event.relatedTarget as? HTMLElement
                // Only the crossing *into* the stack counts. Moving between two toasts, or from a
                // toast to its own close button, must not overwrite the place they came from.
                if (from != null && from.closest("[data-toast-id]") == null) focusOrigin = from
            }
            .then(modifier),
    ) {
        state.rendered.forEach { toast ->
            key(toast.id) {
                LaunchedEffect(toast.dismissing) {
                    if (toast.dismissing) {
                        focusNextToastAfterDismiss(toast.id) {
                            val origin = focusOrigin
                            if (origin != null && document.contains(origin)) {
                                origin.focus()
                            } else {
                                (document.querySelector("[data-toast-host]") as? HTMLElement)?.focus()
                            }
                        }
                    }
                }
                val phase = toastPhase(toast.dismissing)
                LToast(
                    message = toast.message,
                    variant = toast.variant,
                    modifier = Modifier
                        .attr("data-toast-id", toast.id.toString())
                        .attr("data-presence", phase.attributeValue)
                        .then(
                            if (toast.dismissing) {
                                Modifier.attr("inert", "").attr("aria-hidden", "true")
                            } else {
                                Modifier
                            },
                        ),
                    onDismiss = { state.dismiss(toast.id) },
                    onPause = { state.pause(toast.id) },
                    onResume = { state.resume(toast.id) },
                    actionLabel = toast.actionLabel,
                    onAction = toast.onAction,
                )
            }
        }
    }
}

/**
 * The one frame a toast spends below its place before it eases up into it.
 *
 * A transition needs two states to move between, and a toast is born already open — so something
 * has to render the offset first and take it away on the next frame. [rememberLPresence] does
 * exactly this handshake for menus and modals but cannot be used here: it starts a surface that is
 * open on its first composition at [LPresencePhase.PRESENT], which is right for a panel a reader
 * opened and wrong for a message that arrives on its own.
 *
 * If that frame never comes, because the browser has stopped painting this tab, the toast simply
 * sits [Motion.PopoverTravelPx] low — fully opaque, readable, and dismissible. That is the whole
 * reason the entrance moves and does not fade.
 *
 * Under `prefers-reduced-motion` the offset is never rendered at all, rather than rendered and
 * collapsed by the global rule: a reader who asked for less movement gets the toast where it
 * belongs on the first frame.
 */
@Composable
private fun toastPhase(dismissing: Boolean): LPresencePhase {
    var settled by remember { mutableStateOf(prefersReducedMotion()) }
    LaunchedEffect(Unit) {
        if (!settled) {
            suspendCancellableCoroutine<Unit> { continuation ->
                window.requestAnimationFrame { if (continuation.isActive) continuation.resume(Unit) }
            }
            settled = true
        }
    }
    return when {
        dismissing -> LPresencePhase.EXITING
        settled -> LPresencePhase.PRESENT
        else -> LPresencePhase.ENTERING
    }
}

/**
 * Hands focus on before the dismissed toast is detached, or reports that there is nowhere to hand it.
 *
 * Only runs when the toast being dismissed actually holds focus — a toast expiring on its timer
 * while the reader is typing elsewhere must not move anything.
 *
 * [onNothingLeft] is the case that mattered and was missing: a single toast, which is the *normal*
 * case and the guaranteed one for warnings and errors, since those persist until dismissed. The old
 * code searched for a surviving sibling, found none, and returned having moved nothing — leaving
 * focus on a button that is detached a moment later, at which point the browser drops focus to
 * `<body>` and the reader's next Tab restarts at the top of the document.
 */
private fun focusNextToastAfterDismiss(id: Int, onNothingLeft: () -> Unit) {
    val toast = document.querySelector("[data-toast-id='$id']") as? HTMLElement ?: return
    if (!toast.contains(document.activeElement)) return
    val host = toast.parentElement ?: return
    val nodes = host.querySelectorAll("[data-toast-id]")
    val ordered = (0 until nodes.length).mapNotNull { nodes.item(it) as? HTMLElement }
    val from = ordered.indexOf(toast)
    // Forward first, then back. Taking the column's first toast regardless sent a reader who
    // dismissed the third of three backwards past the second, which reads as the stack shuffling
    // under them rather than as one item leaving.
    val survivor = if (from < 0) null else {
        (ordered.drop(from + 1) + ordered.take(from).asReversed())
            .firstOrNull { it.getAttribute("data-presence") != "exiting" }
    }
    val next = survivor?.querySelector("button") as? HTMLElement
    if (next != null) next.focus() else onNothingLeft()
}
